import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DONE_STEP_ID,
  fetchSiteArrivalConfirmationProgress,
  upsertSiteArrivalConfirmationProgress,
} from './siteArrivalConfirmationApi';
import { formatCommitted } from './siteArrivalConfirmationDisplay';
import type {
  SiteArrivalConfirmationFormValues,
  SiteArrivalConfirmationStep,
  YesNo,
} from './siteArrivalConfirmationTypes';

export type SiteArrivalCommittedEntry = {
  stepIndex: number;
  stepId: SiteArrivalConfirmationStep;
  fieldName: keyof SiteArrivalConfirmationFormValues;
  displayText: string;
  rawValue: string;
};

type StepKind = 'choice' | 'date' | 'text' | 'agree';

type StepDef = {
  stepId: SiteArrivalConfirmationStep;
  fieldName: keyof SiteArrivalConfirmationFormValues;
  kind: StepKind;
  required: boolean;
};

const NEXT_ASSISTANT_QUESTION_DELAY_MS = 520;

const DORM_STEPS: SiteArrivalConfirmationStep[] = [
  'rentTv',
  'rentFutons',
  'rentFridge',
  'rentOther',
  'agreeDorm',
];

function usesAccommodation(values: SiteArrivalConfirmationFormValues): boolean {
  return values.companyAccommodation?.trim().toLowerCase() === 'yes';
}

export function buildSteps(values: SiteArrivalConfirmationFormValues): StepDef[] {
  const steps: StepDef[] = [
    {
      stepId: 'travelReceiptAcknowledged',
      fieldName: 'travelReceiptAcknowledged',
      kind: 'choice',
      required: true,
    },
    { stepId: 'healthCheckupDate', fieldName: 'healthCheckupDate', kind: 'date', required: true },
    {
      stepId: 'healthCheckupLocation',
      fieldName: 'healthCheckupLocation',
      kind: 'text',
      required: true,
    },
    {
      stepId: 'companyAccommodation',
      fieldName: 'companyAccommodation',
      kind: 'choice',
      required: true,
    },
  ];

  if (usesAccommodation(values)) {
    steps.push(
      { stepId: 'rentTv', fieldName: 'rentTv', kind: 'choice', required: true },
      { stepId: 'rentFutons', fieldName: 'rentFuton', kind: 'choice', required: true },
      { stepId: 'rentFridge', fieldName: 'rentFridge', kind: 'choice', required: true },
      { stepId: 'rentOther', fieldName: 'rentOther', kind: 'text', required: false },
      { stepId: 'agreeDorm', fieldName: 'agreeDorm', kind: 'agree', required: true }
    );
  }

  steps.push(
    { stepId: 'breakfast', fieldName: 'breakfast', kind: 'choice', required: true },
    { stepId: 'lunch', fieldName: 'lunch', kind: 'choice', required: true },
    { stepId: 'dinner', fieldName: 'dinner', kind: 'choice', required: true },
    { stepId: 'agreeMeal', fieldName: 'agreeMeal', kind: 'agree', required: true }
  );

  return steps;
}

function cursorStepIdForSave(steps: StepDef[], stepIndex: number): string | null {
  if (stepIndex >= steps.length) return DONE_STEP_ID;
  return steps[stepIndex]?.stepId ?? null;
}

function clearDormValues(values: SiteArrivalConfirmationFormValues): SiteArrivalConfirmationFormValues {
  const next = { ...values };
  delete next.rentTv;
  delete next.rentFuton;
  delete next.rentFridge;
  delete next.rentOther;
  delete next.agreeDorm;
  return next;
}

function upsertCommittedEntry(
  committed: SiteArrivalCommittedEntry[],
  entry: SiteArrivalCommittedEntry
): SiteArrivalCommittedEntry[] {
  const next = [...committed];
  const idx = next.findIndex((c) => c.stepId === entry.stepId);
  if (idx >= 0) {
    next[idx] = entry;
    return next;
  }
  next.push(entry);
  return next;
}

function isChoiceAnswered(raw: string | undefined): boolean {
  const v = raw?.trim().toLowerCase();
  return v === 'yes' || v === 'no' || v === 'true' || v === 'false';
}

function findFirstIncompleteDormStepIndex(
  steps: StepDef[],
  vals: SiteArrivalConfirmationFormValues
): number {
  return steps.findIndex((s) => {
    if (!DORM_STEPS.includes(s.stepId)) return false;
    if (s.stepId === 'rentOther') return false;
    if (s.stepId === 'agreeDorm') return vals.agreeDorm !== 'true';
    return !isChoiceAnswered(String(vals[s.fieldName] ?? ''));
  });
}

function rebuildFromProgress(
  values: SiteArrivalConfirmationFormValues,
  currentStepId: string | null | undefined,
  t: (k: string) => string
): { stepIndex: number; committed: SiteArrivalCommittedEntry[]; isComplete: boolean } {
  const steps = buildSteps(values);

  if (currentStepId === DONE_STEP_ID) {
    const committed: SiteArrivalCommittedEntry[] = steps.map((s, i) => {
      const raw = String(values[s.fieldName] ?? '').trim();
      return {
        stepIndex: i,
        stepId: s.stepId,
        fieldName: s.fieldName,
        rawValue: raw,
        displayText: formatCommitted(s.stepId, t, raw, values) || raw,
      };
    });
    return { stepIndex: steps.length, committed, isComplete: true };
  }

  const idx = currentStepId ? steps.findIndex((s) => s.stepId === currentStepId) : -1;
  const stepIndex = idx >= 0 ? idx : 0;
  const committed: SiteArrivalCommittedEntry[] = [];
  for (let i = 0; i < stepIndex; i++) {
    const s = steps[i];
    const raw = String(values[s.fieldName] ?? '').trim();
    committed.push({
      stepIndex: i,
      stepId: s.stepId,
      fieldName: s.fieldName,
      rawValue: raw,
      displayText: formatCommitted(s.stepId, t, raw, values) || raw,
    });
  }
  return { stepIndex, committed, isComplete: false };
}

function isFutureOrTodayDate(iso: string): boolean {
  const m = iso.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return false;
  const picked = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  picked.setHours(0, 0, 0, 0);
  return picked.getTime() >= today.getTime();
}

export function useSiteArrivalConfirmationFlow(
  currentEmail: string | null | undefined,
  invitationId: string | null | undefined,
  t: (k: string) => string
) {
  const [values, setValues] = useState<SiteArrivalConfirmationFormValues>({});
  const [stepIndex, setStepIndex] = useState(0);
  const [committed, setCommitted] = useState<SiteArrivalCommittedEntry[]>([]);
  const [editingCommitIndex, setEditingCommitIndex] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [agreeDormChecked, setAgreeDormChecked] = useState(false);
  const [agreeMealChecked, setAgreeMealChecked] = useState(false);
  const [textDraft, setTextDraft] = useState('');
  const [dateDraft, setDateDraft] = useState('');
  const progressLoadedRef = useRef(false);

  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const assistantHoldTimerRef = useRef<number | null>(null);
  const [suppressNextAssistantBubble, setSuppressNextAssistantBubble] = useState(false);

  const steps = useMemo(() => buildSteps(values), [values]);

  const currentStep: StepDef | undefined = useMemo(() => {
    if (!steps.length) return undefined;
    if (editingCommitIndex !== null) return steps[editingCommitIndex];
    if (stepIndex < steps.length) return steps[stepIndex];
    return undefined;
  }, [steps, stepIndex, editingCommitIndex]);

  const isComplete = steps.length > 0 && stepIndex >= steps.length && editingCommitIndex === null;
  const assistantQuestionVisible = !suppressNextAssistantBubble;

  const clearAssistantHold = useCallback(() => {
    if (assistantHoldTimerRef.current != null) {
      window.clearTimeout(assistantHoldTimerRef.current);
      assistantHoldTimerRef.current = null;
    }
    setSuppressNextAssistantBubble(false);
  }, []);

  useEffect(() => () => clearAssistantHold(), [clearAssistantHold]);

  const persistSnapshot = useCallback(
    async (
      nextValues: SiteArrivalConfirmationFormValues,
      nextStepIndex: number,
      stepsSnapshot: StepDef[]
    ): Promise<boolean> => {
      const email = currentEmail?.trim();
      const invId = invitationId?.trim();
      if (!email || !invId) return true;
      const ok = await upsertSiteArrivalConfirmationProgress({
        email,
        invitationId: invId,
        values: nextValues,
        currentStepId: cursorStepIdForSave(stepsSnapshot, nextStepIndex),
      });
      if (!ok) {
        setSaveError(t('submitDocuments.siteArrivalConfirmation.saveFailed'));
        return false;
      }
      setSaveError(null);
      setValues((prev) => ({ ...prev, ...ok.values }));
      return true;
    },
    [currentEmail, invitationId, t]
  );

  const scheduleNextQuestion = useCallback(() => {
    clearAssistantHold();
    setSuppressNextAssistantBubble(true);
    assistantHoldTimerRef.current = window.setTimeout(() => {
      assistantHoldTimerRef.current = null;
      setSuppressNextAssistantBubble(false);
    }, NEXT_ASSISTANT_QUESTION_DELAY_MS);
  }, [clearAssistantHold]);

  const commitStep = useCallback(
    async (rawValue: string, nextValues: SiteArrivalConfirmationFormValues) => {
      if (!currentStep) return;
      const entry: SiteArrivalCommittedEntry = {
        stepIndex: editingCommitIndex ?? stepIndex,
        stepId: currentStep.stepId,
        fieldName: currentStep.fieldName,
        rawValue,
        displayText: formatCommitted(currentStep.stepId, t, rawValue, nextValues) || rawValue,
      };

      if (editingCommitIndex !== null) {
        const editStepId = currentStep.stepId;
        const wasAccommodation = usesAccommodation(values);
        let adjustedValues = { ...nextValues };

        if (editStepId === 'companyAccommodation' && !usesAccommodation(adjustedValues)) {
          adjustedValues = clearDormValues(adjustedValues);
        }

        const nextSteps = buildSteps(adjustedValues);
        let nextCommitted = upsertCommittedEntry(committed, entry);

        if (editStepId === 'companyAccommodation') {
          if (!usesAccommodation(adjustedValues)) {
            nextCommitted = nextCommitted.filter((c) => !DORM_STEPS.includes(c.stepId));
            setValues(adjustedValues);
            setCommitted(nextCommitted);
            setStepIndex(nextSteps.length);
            setEditingCommitIndex(null);
            await persistSnapshot(adjustedValues, nextSteps.length, nextSteps);
            return;
          }
          if (!wasAccommodation) {
            const dormIdx = findFirstIncompleteDormStepIndex(nextSteps, adjustedValues);
            if (dormIdx >= 0) {
              nextCommitted = nextCommitted.filter((c) => !DORM_STEPS.includes(c.stepId));
              setValues(adjustedValues);
              setCommitted(nextCommitted);
              setStepIndex(dormIdx);
              setEditingCommitIndex(null);
              await persistSnapshot(adjustedValues, dormIdx, nextSteps);
              return;
            }
          }
        }

        setValues(adjustedValues);
        setCommitted(nextCommitted);
        setStepIndex(nextSteps.length);
        setEditingCommitIndex(null);
        if (editStepId === 'agreeDorm') setAgreeDormChecked(rawValue === 'true');
        if (editStepId === 'agreeMeal') setAgreeMealChecked(rawValue === 'true');
        await persistSnapshot(adjustedValues, nextSteps.length, nextSteps);
        return;
      }

      scheduleNextQuestion();
      const nextSteps = buildSteps(nextValues);
      const nextIdx = stepIndex + 1;
      setValues(nextValues);
      setCommitted((prev) => [...prev, entry]);
      setStepIndex(nextIdx);
      await persistSnapshot(nextValues, nextIdx, nextSteps);
    },
    [
      currentStep,
      editingCommitIndex,
      stepIndex,
      committed,
      t,
      scheduleNextQuestion,
      persistSnapshot,
    ]
  );

  const advanceChoice = useCallback(
    async (choice: YesNo) => {
      if (!currentStep || currentStep.kind !== 'choice') return;
      let nextValues: SiteArrivalConfirmationFormValues = {
        ...values,
        [currentStep.fieldName]: choice,
      };
      if (currentStep.stepId === 'companyAccommodation' && choice === 'no') {
        nextValues = clearDormValues(nextValues);
      }
      await commitStep(choice, nextValues);
    },
    [currentStep, values, commitStep]
  );

  const advanceDate = useCallback(async () => {
    if (!currentStep || currentStep.kind !== 'date') return;
    const iso = dateDraft.trim();
    if (!isFutureOrTodayDate(iso)) {
      setDateError(t('submitDocuments.siteArrivalConfirmation.dateMustBeFuture'));
      return;
    }
    setDateError(null);
    const nextValues = { ...values, healthCheckupDate: iso };
    await commitStep(iso, nextValues);
  }, [currentStep, dateDraft, values, commitStep, t]);

  const advanceText = useCallback(async () => {
    if (!currentStep || currentStep.kind !== 'text') return;
    const text = textDraft.trim();
    if (currentStep.required && !text) return;
    const nextValues = { ...values, [currentStep.fieldName]: text };
    await commitStep(text, nextValues);
    setTextDraft('');
  }, [currentStep, textDraft, values, commitStep]);

  const advanceAgreeDorm = useCallback(async () => {
    if (!agreeDormChecked || currentStep?.stepId !== 'agreeDorm') return;
    const nextValues = { ...values, agreeDorm: 'true' };
    await commitStep('true', nextValues);
  }, [agreeDormChecked, currentStep, values, commitStep]);

  const advanceAgreeMeal = useCallback(async () => {
    if (!agreeMealChecked || currentStep?.stepId !== 'agreeMeal') return;
    const nextValues = { ...values, agreeMeal: 'true' };
    await commitStep('true', nextValues);
  }, [agreeMealChecked, currentStep, values, commitStep]);

  useEffect(() => {
    if (!currentEmail?.trim() || !invitationId?.trim() || progressLoadedRef.current) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const data = await fetchSiteArrivalConfirmationProgress(currentEmail, invitationId);
        if (cancelled || !data) return;
        progressLoadedRef.current = true;
        const loadedValues = data.values ?? {};
        setValues(loadedValues);
        const rebuilt = rebuildFromProgress(loadedValues, data.currentStepId, t);
        setCommitted(rebuilt.committed);
        setStepIndex(rebuilt.stepIndex);
        setAgreeDormChecked(loadedValues.agreeDorm === 'true');
        setAgreeMealChecked(loadedValues.agreeMeal === 'true');
      } catch {
        progressLoadedRef.current = true;
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentEmail, invitationId, t]);

  useEffect(() => {
    if (!currentStep) return;
    if (currentStep.kind === 'text') {
      const existing = String(values[currentStep.fieldName] ?? '');
      setTextDraft(existing);
    }
    if (currentStep.kind === 'date') {
      const existing = values.healthCheckupDate?.trim() ?? '';
      if (existing) {
        setDateDraft(existing);
      } else {
        const today = new Date();
        const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        setDateDraft(iso);
      }
    }
    if (currentStep.stepId === 'agreeDorm') {
      setAgreeDormChecked(values.agreeDorm === 'true');
    }
    if (currentStep.stepId === 'agreeMeal') {
      setAgreeMealChecked(values.agreeMeal === 'true');
    }
  }, [currentStep, values]);

  const undo = useCallback(() => {
    clearAssistantHold();
    setCommitted((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      const nextStepIndex = prev.length - 1;
      setValues((v) => {
        let next = { ...v };
        delete next[last.fieldName];
        if (last.stepId === 'companyAccommodation') {
          next = clearDormValues(next);
        }
        if (last.stepId === 'agreeDorm') setAgreeDormChecked(false);
        if (last.stepId === 'agreeMeal') {
          next.agreeMeal = 'false';
          setAgreeMealChecked(false);
        }
        void persistSnapshot(next, nextStepIndex, buildSteps(next));
        return next;
      });
      setStepIndex(nextStepIndex);
      setEditingCommitIndex(null);
      return prev.slice(0, -1);
    });
  }, [clearAssistantHold, persistSnapshot]);

  const showEditForCommitIndex = useCallback(
    (commitIndex: number) => {
      if (editingCommitIndex !== null) return false;
      if (!isComplete || committed.length === 0) return false;
      return commitIndex >= 0 && commitIndex < committed.length;
    },
    [editingCommitIndex, isComplete, committed.length]
  );

  const jumpToEditCommitIndex = useCallback(
    (commitIndex: number) => {
      if (!isComplete || commitIndex < 0 || commitIndex >= committed.length) return;
      const entry = committed[commitIndex];
      const stepIdx = steps.findIndex((s) => s.stepId === entry.stepId);
      if (stepIdx < 0) return;
      clearAssistantHold();
      if (entry.stepId === 'agreeDorm') setAgreeDormChecked(entry.rawValue === 'true');
      if (entry.stepId === 'agreeMeal') setAgreeMealChecked(entry.rawValue === 'true');
      setEditingCommitIndex(stepIdx);
    },
    [isComplete, committed, steps, clearAssistantHold]
  );

  const showUndoForCommitIndex = useCallback(
    (commitIndex: number) => {
      if (editingCommitIndex !== null) return false;
      if (isComplete && committed.length > 0) return commitIndex === committed.length - 1;
      return stepIndex === commitIndex + 1;
    },
    [editingCommitIndex, isComplete, committed.length, stepIndex]
  );

  const handleFinalComplete = useCallback(async (): Promise<boolean> => {
    const email = currentEmail?.trim();
    const invId = invitationId?.trim();
    if (!email || !invId) return true;
    const ok = await upsertSiteArrivalConfirmationProgress({
      email,
      invitationId: invId,
      values: { ...values, agreeMeal: 'true' },
      currentStepId: DONE_STEP_ID,
    });
    if (!ok) {
      setSaveError(t('submitDocuments.siteArrivalConfirmation.saveFailed'));
      return false;
    }
    setValues((prev) => ({ ...prev, ...ok.values }));
    return true;
  }, [currentEmail, invitationId, values, t]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [committed, stepIndex, isComplete, suppressNextAssistantBubble]);

  return {
    t,
    values,
    committed,
    currentStep,
    stepIndex,
    isLoading,
    isComplete,
    agreeDormChecked,
    setAgreeDormChecked,
    agreeMealChecked,
    setAgreeMealChecked,
    textDraft,
    setTextDraft,
    dateDraft,
    setDateDraft,
    dateError,
    saveError,
    threadEndRef,
    assistantQuestionVisible,
    advanceChoice,
    advanceDate,
    advanceText,
    advanceAgreeDorm,
    advanceAgreeMeal,
    undo,
    showEditForCommitIndex,
    jumpToEditCommitIndex,
    showUndoForCommitIndex,
    editingCommitIndex,
    handleFinalComplete,
    usesAccommodation: usesAccommodation(values),
  };
}
